/**
 * Shared helpers for capabilities that own their own questions
 * (no named sub-capabilities) versus the usual capability → child flow.
 */

export const isLeafCapability = (capability) =>
  !(capability?.subCapabilities || []).some(
    (sub) =>
      Number(sub?.subId || sub?.id || 0) > 0 ||
      String(sub?.subCapabilityName || sub?.name || '').trim()
  );

export const isLeafUnit = (subCapabilityId) =>
  subCapabilityId == null || Number(subCapabilityId) === 0;

export const isLeafSelectedCapability = (capability) => {
  const units = capability?.selectedSubCapabilities || [];
  return units.length > 0 && units.every((unit) => isLeafUnit(unit.subCapabilityId));
};

export const assessmentUnitKey = (capabilityId, subCapabilityId) =>
  isLeafUnit(subCapabilityId)
    ? `cap-${Number(capabilityId) || 0}`
    : `sub-${Number(subCapabilityId)}`;

export const parseAssessmentUnitKey = (key: string | null | undefined) => {
  if (!key || typeof key !== 'string') {
    return null;
  }
  if (key.startsWith('cap-')) {
    const capabilityId = Number(key.slice(4));
    if (!Number.isFinite(capabilityId)) {
      return null;
    }
    return { capabilityId, subCapabilityId: 0 as const };
  }
  if (key.startsWith('sub-')) {
    const subCapabilityId = Number(key.slice(4));
    if (!Number.isFinite(subCapabilityId)) {
      return null;
    }
    return { capabilityId: null, subCapabilityId };
  }
  return null;
};

export const sameAssessmentUnit = (leftCapId, leftSubId, rightCapId, rightSubId) =>
  Number(leftCapId) === Number(rightCapId) &&
  ((isLeafUnit(leftSubId) && isLeafUnit(rightSubId)) ||
    Number(leftSubId) === Number(rightSubId));

export const selectableUnitCount = (capability) =>
  isLeafCapability(capability) ? 1 : (capability?.subCapabilities || []).length;

export const flattenAssessmentRows = (raw) => {
  const list = Array.isArray(raw) ? raw : [];
  const rows = [];
  list.forEach((item) => {
    if (Array.isArray(item?.subCapabilitiesAssessments) && item.subCapabilitiesAssessments.length) {
      rows.push(...item.subCapabilitiesAssessments);
    } else if (item) {
      rows.push(item);
    }
  });
  return rows;
};

export const findAssessmentRow = (rows, capabilityId, subCapabilityId) =>
  (rows || []).find((item) =>
    sameAssessmentUnit(
      item?.capabilityId,
      item?.subCapabilityId,
      capabilityId,
      subCapabilityId
    )
  );

export const lookupUnitState = (map, capabilityId, subCapabilityId) => {
  if (!map) return undefined;
  return map[assessmentUnitKey(capabilityId, subCapabilityId)];
};

export const assessmentUnitTitle = (capabilityId, subCapabilityId, name) =>
  isLeafUnit(subCapabilityId) ? `Capability: ${name || ''}` : `Sub Capability: ${name || ''}`;

/** Sidebar highlight for capability rows (including direct-question / leaf capabilities). */
export const isCapabilityNavHighlighted = (
  capabilityId,
  {
    leaf,
    firstSubCapabilityId,
    selectedCard,
    subSelectedCard,
    activeUnitKey,
  }
) => {
  if (selectedCard == null || Number(selectedCard) !== Number(capabilityId)) {
    return false;
  }
  if (leaf) {
    const leafKey = assessmentUnitKey(capabilityId, firstSubCapabilityId ?? null);
    return subSelectedCard === leafKey || activeUnitKey === leafKey;
  }
  // Non-leaf: parent row highlights only when a sub-thread under this capability is active
  return subSelectedCard != null;
};
